// ===========================================
// USERS SERVICE - Production Ready
// ===========================================

import api from './api';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatar?: string;
  phone?: string;
  role: 'ADMIN' | 'MANAGER' | 'AGENT' | 'VIEWER';
  status: 'ACTIVE' | 'INACTIVE' | 'PENDING' | 'SUSPENDED';
  department?: string;
  jobTitle?: string;
  timezone?: string;
  language?: string;
  permissions?: string[];
  lastLoginAt?: string;
  lastActivityAt?: string;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  organizationId: string;
  organization?: {
    id: string;
    name: string;
    slug: string;
  };
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface UsersListResponse {
  users: User[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UserFilters {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  department?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface CreateUserData {
  email: string;
  firstName: string;
  lastName: string;
  role?: string;
  phone?: string;
  department?: string;
  jobTitle?: string;
  sendInvite?: boolean;
}

export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  status?: string;
  department?: string;
  jobTitle?: string;
  timezone?: string;
  language?: string;
  permissions?: string[];
}

export interface UserActivity {
  id: string;
  type: string;
  action: string;
  description: string;
  entityType?: string;
  entityId?: string;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

export interface UserStats {
  totalContacts: number;
  totalLeads: number;
  totalBookings: number;
  totalRevenue: number;
  conversionRate: number;
  activeDeals: number;
  tasksCompleted: number;
  tasksPending: number;
}

export const usersService = {
  // Users CRUD
  async list(filters: UserFilters = {}) {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    return api.get<UsersListResponse>(`/api/users?${params.toString()}`);
  },

  async getById(id: string) {
    return api.get<User>(`/api/users/${id}`);
  },

  async getMe() {
    return api.get<User>('/api/users/me');
  },

  async create(data: CreateUserData) {
    return api.post<User>('/api/users', data);
  },

  async update(id: string, data: UpdateUserData) {
    return api.patch<User>(`/api/users/${id}`, data);
  },

  async updateMe(data: UpdateUserData) {
    return api.patch<User>('/api/users/me', data);
  },

  async delete(id: string) {
    return api.delete(`/api/users/${id}`);
  },

  // Status Management
  async activate(id: string) {
    return api.post<User>(`/api/users/${id}/activate`);
  },

  async deactivate(id: string) {
    return api.post<User>(`/api/users/${id}/deactivate`);
  },

  async suspend(id: string, reason?: string) {
    return api.post<User>(`/api/users/${id}/suspend`, { reason });
  },

  // Invitations
  async invite(data: {
    email: string;
    firstName?: string;
    lastName?: string;
    role?: string;
    message?: string;
    department?: string;
    jobTitle?: string;
  }) {
    // Backend team invite lives under org routes (not /api/users/invite)
    const emailLocal = (data.email || '').split('@')[0] || 'Member';
    const [guessFirst, ...rest] = emailLocal.split(/[._-]/).filter(Boolean);
    return api.post<{ user: { id: string; email: string } }>('/api/org/team/invite', {
      email: data.email,
      firstName: data.firstName || guessFirst || 'Team',
      lastName: data.lastName || rest.join(' ') || 'Member',
      role: data.role || 'MEMBER',
      department: data.department,
      jobTitle: data.jobTitle,
    });
  },

  async resendInvite(userId: string) {
    return api.post(`/api/users/${userId}/resend-invite`);
  },

  async cancelInvite(invitationId: string) {
    return api.delete(`/api/users/invitations/${invitationId}`);
  },

  async listInvitations() {
    return api.get<{
      invitations: {
        id: string;
        email: string;
        role: string;
        status: string;
        expiresAt: string;
        createdAt: string;
      }[];
    }>('/api/users/invitations');
  },

  // Avatar
  async uploadAvatar(file: File) {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await fetch('/api/users/me/avatar', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      body: formData,
    });

    return response.json();
  },

  async removeAvatar() {
    return api.delete('/api/users/me/avatar');
  },

  // Password & Security
  async changePassword(currentPassword: string, newPassword: string) {
    return api.post('/api/auth/change-password', { currentPassword, newPassword });
  },

  async resetPassword(userId: string) {
    return api.post(`/api/users/${userId}/reset-password`);
  },

  async enable2FA() {
    return api.post<{
      success: boolean;
      qrCode: string;
      secret: string;
      backupCodes?: string[];
    }>('/api/auth/2fa/enable');
  },

  async verify2FA(code: string) {
    return api.post<{ success: boolean; backupCodes?: string[] }>('/api/auth/2fa/verify', { token: code });
  },

  async disable2FA(code: string) {
    return api.post('/api/auth/2fa/disable', { token: code });
  },

  async regenerateBackupCodes(code: string) {
    return api.post<{ backupCodes: string[] }>('/api/users/me/2fa/backup-codes', { code });
  },

  // Activity & Sessions
  async getActivity(userId: string, page = 1, limit = 20) {
    return api.get<{
      activities: UserActivity[];
      total: number;
      page: number;
      limit: number;
    }>(`/api/users/${userId}/activity?page=${page}&limit=${limit}`);
  },

  async getMyActivity(page = 1, limit = 20) {
    return api.get<{
      activities: UserActivity[];
      total: number;
      page: number;
      limit: number;
    }>(`/api/users/me/activity?page=${page}&limit=${limit}`);
  },

  async getSessions() {
    return api.get<{
      sessions: {
        id: string;
        deviceType: string;
        browser: string;
        os: string;
        ipAddress: string;
        location?: string;
        isCurrent: boolean;
        lastActiveAt: string;
        createdAt: string;
      }[];
    }>('/api/users/me/sessions');
  },

  async revokeSession(sessionId: string) {
    return api.delete(`/api/users/me/sessions/${sessionId}`);
  },

  async revokeAllSessions() {
    return api.delete('/api/users/me/sessions');
  },

  // Permissions & Roles
  async getRoles() {
    return api.get<{
      roles: {
        id: string;
        name: string;
        description: string;
        permissions: string[];
        isSystem: boolean;
      }[];
    }>('/api/users/roles');
  },

  async getPermissions() {
    return api.get<{
      permissions: {
        id: string;
        name: string;
        description: string;
        category: string;
      }[];
    }>('/api/users/permissions');
  },

  async updateUserPermissions(userId: string, permissions: string[]) {
    return api.patch<User>(`/api/users/${userId}/permissions`, { permissions });
  },

  async updateUserRole(userId: string, role: string) {
    return api.patch<User>(`/api/users/${userId}/role`, { role });
  },

  // Stats
  async getUserStats(userId: string) {
    return api.get<UserStats>(`/api/users/${userId}/stats`);
  },

  async getMyStats() {
    return api.get<UserStats>('/api/users/me/stats');
  },

  async getTeamStats() {
    return api.get<{
      totalUsers: number;
      activeUsers: number;
      byRole: Record<string, number>;
      byDepartment: Record<string, number>;
      newUsersThisMonth: number;
      avgPerformance: number;
    }>('/api/users/team/stats');
  },

  // Preferences
  async getPreferences() {
    return api.get<{
      notifications: {
        email: boolean;
        push: boolean;
        sms: boolean;
        marketing: boolean;
      };
      display: {
        theme: string;
        language: string;
        timezone: string;
        dateFormat: string;
        currency: string;
      };
      privacy: {
        showProfile: boolean;
        showActivity: boolean;
      };
    }>('/api/users/me/preferences');
  },

  async updatePreferences(preferences: Record<string, unknown>) {
    return api.patch('/api/users/me/preferences', preferences);
  },

  // Export
  async export(format: 'csv' | 'xlsx' = 'csv', filters?: UserFilters) {
    const params = new URLSearchParams({ format });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, String(value));
        }
      });
    }
    
    const response = await fetch(`/api/users/export?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    return response.blob();
  },
};

export default usersService;
